"use client";

import { ChevronLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { formatBytes } from "../_components/format";
import { MediaDropzone } from "../_components/MediaDropzone";
import { MediaWorkspace } from "../_components/MediaWorkspace";
import { acceptForKinds, mediaKindOf } from "../_components/media";
import { visibleToolCatalog, visibleToolCatalogById } from "../_operations/catalog";
import { ALL_OPERATIONS, type AnyOperation } from "../_operations/registry";
import type { MediaKind } from "../_operations/types";

/** Operations offered for a dropped file: single-input ops that accept its kind. */
function applicableOperations(kind: MediaKind): AnyOperation[] {
  const visibleToolIds = new Set(visibleToolCatalog().map((tool) => tool.id));
  return ALL_OPERATIONS.filter(
    (op) => visibleToolIds.has(op.id) && !op.multiple && op.inputKinds.includes(kind)
  );
}

export function StudioWorkspace() {
  const t = useTranslations("mediaTools");
  const [file, setFile] = useState<File | null>(null);
  const [operation, setOperation] = useState<AnyOperation | null>(null);

  if (!file) {
    return (
      <MediaDropzone
        accept={acceptForKinds(["audio", "video"])}
        prompt={t("studio.dropPrompt")}
        onDrop={(files) => files[0] && setFile(files[0])}
      />
    );
  }

  if (!operation) {
    const kind = mediaKindOf(file);
    const ops = applicableOperations(kind);
    const audioOps = ops.filter((op) => op.group === "audio");
    const videoOps = ops.filter((op) => op.group === "video");

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 rounded-lg border bg-card p-4">
          <p className="min-w-0 truncate text-sm">
            <span className="font-medium">{file.name}</span>{" "}
            <span className="text-foreground/50">({formatBytes(file.size)})</span>
          </p>
          <Button variant="ghost" size="sm" onClick={() => setFile(null)}>
            {t("studio.changeFile")}
          </Button>
        </div>

        <p className="text-foreground/70">{t("studio.pickTool")}</p>
        {videoOps.length > 0 && (
          <OperationGroup title={t("groups.video")} ops={videoOps} onPick={setOperation} />
        )}
        {audioOps.length > 0 && (
          <OperationGroup title={t("groups.audio")} ops={audioOps} onPick={setOperation} />
        )}
      </div>
    );
  }

  const entry = visibleToolCatalogById()[operation.id];
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" size="sm" onClick={() => setOperation(null)}>
          <ChevronLeft className="mr-1 h-4 w-4" /> {t("studio.otherTools")}
        </Button>
        <p className="text-sm font-medium">{entry ? t(`catalog.${entry.id}.title`) : null}</p>
      </div>
      <MediaWorkspace key={operation.id} operation={operation} initialFiles={[file]} />
    </div>
  );
}

function OperationGroup({
  title,
  ops,
  onPick,
}: {
  title: string;
  ops: AnyOperation[];
  onPick: (op: AnyOperation) => void;
}) {
  const t = useTranslations("mediaTools");
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-foreground/60">{title}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {ops.map((op) => {
          const entry = visibleToolCatalogById()[op.id];
          return (
            <button
              key={op.id}
              type="button"
              onClick={() => onPick(op)}
              className="rounded-lg border bg-card p-4 text-left transition-colors hover:border-foreground hover:bg-foreground/5"
            >
              <p className="font-medium">{entry ? t(`catalog.${entry.id}.title`) : null}</p>
              <p className="text-sm text-foreground/60">
                {entry ? t(`catalog.${entry.id}.summary`) : null}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
